import { useEffect, useMemo } from "react";
import { useSession } from "../../state/session.js";
import { isTypingTarget, moveInput } from "../camera/input.js";
import { navStore } from "../camera/nav.js";
import { useWorldState, worldStore } from "../store.js";
import { DriveHud } from "./DriveHud.js";
import { GaleriPanel } from "./GaleriPanel.js";
import { useVehicleT } from "./i18n.js";
import { TopUpDialog, WalletPill } from "./TopUp.js";
import { useVehicles, vehicleStore } from "./store.js";
import { installVehicleHook } from "./hook.js";
import "./vehicles.css";

/**
 * The DOM side of vehicles, mounted once next to the navigation HUD: the Galeri panel, the "E – Get
 * in" prompt, the driving HUD and the keyboard (E, 6). The 3D side is `VehiclesLayer` and
 * `DriveRig` inside the canvas.
 */
export function VehiclesUi() {
  const { user } = useSession();
  useEffect(() => {
    if (user?.id) vehicleStore.setSelf(user.id);
  }, [user?.id]);
  // The catalog and the user's cars load once the world is on screen.
  const status = useWorldState(worldStore).status;
  useEffect(() => {
    if (status === "ready") void vehicleStore.refresh();
  }, [status]);
  useVehicleKeys();
  useEffect(() => installVehicleHook(), []);
  return (
    <>
      <Prompt />
      <WalletPill />
      <GaleriPanel />
      <DriveHud />
      <TopUpDialog />
    </>
  );
}

/** E gets in a nearby car or opens the dealership (before the door key sees it), E again gets out. */
function useVehicleKeys(): void {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      const s = vehicleStore.getState();
      if (e.key === "6" && s.drivingId) {
        vehicleStore.cockpit = !vehicleStore.cockpit;
        e.stopImmediatePropagation();
        return;
      }
      if (s.drivingId && /^[1-5]$/.test(e.key)) {
        // The other camera modes would leave the car running with no one at the wheel.
        e.stopImmediatePropagation();
        return;
      }
      if (e.key === "6") {
        // Mode 6 only exists inside a vehicle.
        e.stopImmediatePropagation();
        return;
      }
      if (s.topUp.open) return;
      if (e.code === "Escape" && s.galeri.open) {
        vehicleStore.closeGaleri();
        return;
      }
      if (e.code !== "KeyE") return;
      if (s.drivingId) {
        e.stopImmediatePropagation();
        moveInput.reset();
        void vehicleStore.exit();
        return;
      }
      if (s.galeri.open) return;
      if (s.near && navStore.getState().inside === null) {
        e.stopImmediatePropagation();
        activate(s.near);
      }
    };
    // Capture phase on window: runs before the navigation key handler, which would walk into a door.
    window.addEventListener("keydown", down, true);
    return () => window.removeEventListener("keydown", down, true);
  }, []);
}

export function activate(
  near: NonNullable<ReturnType<typeof vehicleStore.getState>["near"]>,
): void {
  if (near.kind === "vehicle") void vehicleStore.enter(near.id);
  else vehicleStore.openGaleri(near.id);
}

function Prompt() {
  const t = useVehicleT();
  const near = useVehicles((s) => s.near);
  const driving = useVehicles((s) => s.drivingId);
  const open = useVehicles((s) => s.galeri.open);
  const coarse = useMemo(() => globalThis.matchMedia?.("(pointer: coarse)").matches ?? false, []);
  if (!near || driving || open) return null;
  const text =
    near.kind === "vehicle"
      ? coarse
        ? t("tapGetIn")
        : t("pressE")
      : coarse
        ? t("tapGaleri")
        : t("pressEGaleri");
  return (
    <button
      type="button"
      className="veh-prompt"
      data-testid="vehicle-prompt"
      data-kind={near.kind}
      onClick={() => activate(near)}
    >
      {text}
    </button>
  );
}
